import { useState } from "react";
import { ActionIcon, Button, Card, Grid, Group, NumberInput, Stack, Table, Text, Textarea, TextInput } from "@mantine/core";
import { IconLink, IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import type { IngredientExtractArguments, OutgoingSearchResult } from "@passio/api-client";
import { passio } from "../lib/clients";
import { PageHeader } from "../theme/components";
import { EmptyState, ErrorNotice, RequireFeature, ResultHeader } from "../components/common";
import { FoodDetailDrawer, FoodRow } from "../components/food";
import { RouteModelPicker } from "../components/ModelPicker";

interface Row {
  ingredientName: string;
  portionQuantity: number | "";
  portionSize: string;
  brandName: string;
}

const START: Row[] = [
  { ingredientName: "rolled oats", portionQuantity: 0.5, portionSize: "cup", brandName: "" },
  { ingredientName: "greek yogurt", portionQuantity: 170, portionSize: "g", brandName: "Chobani" },
  { ingredientName: "blueberries", portionQuantity: 1, portionSize: "handful", brandName: "" },
  { ingredientName: "honey", portionQuantity: 1, portionSize: "tbsp", brandName: "" },
];

const toNumber = (s: string) => {
  if (!s.includes("/")) return Number(s.replace(",", "."));
  const [a, b] = s.split("/").map(Number);
  return b ? a / b : NaN;
};

/** "2 cups flour" / "1/2 tbsp olive oil" per line to rows. */
function parseLines(text: string): Row[] {
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const m = l.match(/^([\d.,/]+)\s*([a-zA-Z]+)?\s+(.*)$/);
      if (!m) return { ingredientName: l, portionQuantity: "", portionSize: "", brandName: "" };
      return { ingredientName: m[3], portionQuantity: toNumber(m[1]) || "", portionSize: m[2] ?? "", brandName: "" };
    });
}

export function LinkIngredientsPage() {
  const [rows, setRows] = useState<Row[]>(START);
  const [paste, setPaste] = useState("");
  const [selected, setSelected] = useState<OutgoingSearchResult | null>(null);
  const link = useMutation({
    mutationFn: (items: IngredientExtractArguments[]) => passio.foods.linkIngredients(items),
  });
  const set = (i: number, patch: Partial<Row>) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const submit = () =>
    link.mutate(
      rows
        .filter((r) => r.ingredientName.trim())
        .map((r) => ({
          ingredientName: r.ingredientName.trim(),
          ...(r.portionQuantity !== "" ? { portionQuantity: r.portionQuantity } : {}),
          ...(r.portionSize ? { portionSize: r.portionSize } : {}),
          ...(r.brandName ? { brandName: r.brandName, isSpecificBrandedProduct: true } : {}),
        })),
    );

  return (
    <RequireFeature feature="search">
      <PageHeader eyebrow="POST /napi/food/search/linkIngredients" title="Link ingredients" description="Already have a structured list (a recipe, a cart, your own parser)? Match every line to a database food in one call." actions={<RouteModelPicker routes={["linkIngredients"]} />} />
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <Card>
            <Table verticalSpacing={6}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Ingredient</Table.Th>
                  <Table.Th w={90}>Qty</Table.Th>
                  <Table.Th w={110}>Unit</Table.Th>
                  <Table.Th w={130}>Brand</Table.Th>
                  <Table.Th w={36} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((r, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <TextInput size="xs" value={r.ingredientName} onChange={(e) => set(i, { ingredientName: e.currentTarget.value })} />
                    </Table.Td>
                    <Table.Td>
                      <NumberInput size="xs" hideControls value={r.portionQuantity} onChange={(v) => set(i, { portionQuantity: v === "" ? "" : Number(v) })} />
                    </Table.Td>
                    <Table.Td>
                      <TextInput size="xs" value={r.portionSize} onChange={(e) => set(i, { portionSize: e.currentTarget.value })} />
                    </Table.Td>
                    <Table.Td>
                      <TextInput size="xs" placeholder="optional" value={r.brandName} onChange={(e) => set(i, { brandName: e.currentTarget.value })} />
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon variant="subtle" color="gray" onClick={() => setRows((x) => x.filter((_, j) => j !== i))} aria-label="Remove row">
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Group justify="space-between" mt="sm">
              <Button variant="subtle" color="ink" size="xs" leftSection={<IconPlus size={14} />} onClick={() => setRows((r) => [...r, { ingredientName: "", portionQuantity: "", portionSize: "", brandName: "" }])}>
                Add row
              </Button>
              <Button leftSection={<IconLink size={16} />} onClick={submit} loading={link.isPending} disabled={!rows.some((r) => r.ingredientName.trim())}>
                Link {rows.filter((r) => r.ingredientName.trim()).length} ingredients
              </Button>
            </Group>
            <Stack gap={6} mt="lg">
              <Textarea label="Or paste a list" description="One ingredient per line, e.g. “2 cups flour”" autosize minRows={3} value={paste} onChange={(e) => setPaste(e.currentTarget.value)} />
              <Button variant="default" size="xs" w="fit-content" disabled={!paste.trim()} onClick={() => (setRows(parseLines(paste)), setPaste(""))}>
                Replace rows from text
              </Button>
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 5 }}>
          {link.error && <ErrorNotice error={link.error} onRetry={submit} />}
          {link.data ? (
            <Card>
              <ResultHeader title="Matches" count={link.data.data.results?.length ?? 0} meta={link.data.meta} />
              <Stack gap={2}>
                {(link.data.data.results ?? []).map((r, i) => (
                  <FoodRow key={i} item={r} onClick={r.refCode ? () => setSelected(r) : undefined} />
                ))}
              </Stack>
              {!link.data.data.results?.length && <Text c="dimmed">No matches.</Text>}
            </Card>
          ) : (
            !link.error && <EmptyState title="Matches appear here">Each line comes back as a search result with a refCode you can hydrate.</EmptyState>
          )}
        </Grid.Col>
      </Grid>
      <FoodDetailDrawer refCode={selected?.refCode} name={selected?.displayName} amount={selected} onClose={() => setSelected(null)} />
    </RequireFeature>
  );
}
